import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import UserAvatar from './userAvatar'
import StoryViewer from './storyViewer'
import { hasStory } from '../utils/story'
import * as followActions from '../data/follow_data'
import '../styles/story.css'

const labelOf = (user) => user?.name || user?.email || 'Story'

const StoriesRail = ({ variant = 'feed', hidden = false, peekHost = null }) => {
  const dispatch = useDispatch()
  const me = useSelector(state => state.authReduse)
  const follow = useSelector(state => state.followReduse.follow || [])
  const [openIndex, setOpenIndex] = useState(-1)
  const [ownOpen, setOwnOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (!me.id) return undefined
    dispatch(followActions.get_follow()).catch(() => {})
    return undefined
  }, [dispatch, me.id])

  useEffect(() => {
    if (hidden) setExpanded(false)
  }, [hidden])

  const followedStories = useMemo(
    () => (Array.isArray(follow) ? follow : []).filter(user => user && hasStory(user.story)),
    [follow]
  )

  const queue = useMemo(() => {
    const rows = followedStories.map(user => ({
      id: user.userid || user.id,
      story: user.story,
      name: labelOf(user),
      photo: user.photo,
      canAdd: false
    }))
    if (me.id && hasStory(me.story)) {
      rows.unshift({
        id: me.id,
        story: me.story,
        name: 'Your story',
        photo: me.photo,
        canAdd: true
      })
    }
    return rows
  }, [followedStories, me.id, me.story, me.photo])

  const storyCount = followedStories.length
  const showPeek = !expanded && storyCount > 0
  const showRail = expanded

  if (!me.id && !followedStories.length) {
    if (variant === 'inbox') {
      return <div className="storiesRailWrap storiesRailWrap--inbox storiesRailWrap--empty" aria-hidden="true" />
    }
    return null
  }

  const openFollowed = (userId) => {
    const idx = queue.findIndex(item => String(item.id) === String(userId))
    if (idx >= 0) setOpenIndex(idx)
  }

  const mode = showRail ? ' isOpen' : showPeek ? ' isPeek' : ' isHidden'

  const peekButton = showPeek ? (
    <button
      type="button"
      className={`storiesPeek storiesPeek--${variant}`}
      onClick={() => setExpanded(true)}
      aria-label={`${storyCount} stories`}
    >
      {storyCount}
    </button>
  ) : null

  const peekEl = !peekButton
    ? null
    : peekHost
      ? createPortal(peekButton, peekHost)
      : variant === 'inbox'
        ? null
        : peekButton

  return (
    <div className={`storiesRailWrap storiesRailWrap--${variant}${mode}`}>
      {peekEl}

      {showRail ? (
        <div
          className={`storiesRail storiesRail--${variant}`}
          aria-label="Stories"
          onWheel={event => {
            const node = event.currentTarget
            if (!node) return
            if (Math.abs(event.deltaY) >= Math.abs(event.deltaX)) {
              node.scrollLeft += event.deltaY
            }
          }}
        >
          {me.id ? (
            <button
              type="button"
              className="storiesRailItem"
              onClick={() => {
                if (hasStory(me.story)) openFollowed(me.id)
                else setOwnOpen(true)
              }}
            >
              <span className={`storyRing storiesRailFace${hasStory(me.story) ? ' hasStory' : ' canAdd'}`}>
                <UserAvatar className="storiesRailImg" src={me.photo} name={me.email || me.name} alt="" />
                {!hasStory(me.story) ? <span className="storyAddBadge storiesRailAdd">+</span> : null}
              </span>
              <em>Your story</em>
            </button>
          ) : null}

          {followedStories.map(user => (
            <button
              type="button"
              key={user.userid || user.id}
              className="storiesRailItem"
              onClick={() => openFollowed(user.userid || user.id)}
            >
              <span className="storyRing storiesRailFace hasStory">
                <UserAvatar className="storiesRailImg" src={user.photo} name={labelOf(user)} alt="" />
              </span>
              <em>{labelOf(user)}</em>
            </button>
          ))}
        </div>
      ) : null}

      <StoryViewer
        open={ownOpen}
        onClose={() => setOwnOpen(false)}
        media={me.story}
        name={me.email || me.name}
        photo={me.photo}
        canAdd
      />
      <StoryViewer
        open={openIndex >= 0}
        onClose={() => setOpenIndex(-1)}
        items={queue}
        index={openIndex}
        onIndex={setOpenIndex}
      />
    </div>
  )
}

export default StoriesRail
